import React, { useState, useRef, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ArrowRight, Home, Building2, Users, Calendar, Shield, DollarSign, FileText, User } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import ImageUploader from './ImageUploader';

export default function QuestionnaireForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1
    service: '',
    // Step 2
    property_type: '',
    // Step 3
    stories: '',
    // Step 4
    timeline: '',
    // Step 5
    insurance_claim: '',
    // Step 6
    budget_range: '',
    // Step 7
    project_description: '',
    repair_types: [],
    // Step 8
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  // Scroll to top of form when step changes
  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }
  }, [currentStep]);

  const services = [
    { id: 'roof-repair', label: 'Roof Repair', icon: <Home className="h-8 w-8" /> },
    { id: 'roof-replacement', label: 'Roof Replacement', icon: <Home className="h-8 w-8" /> },
    { id: 'emergency-tarp', label: 'Emergency Tarp', icon: <Shield className="h-8 w-8" /> },
    { id: 'stucco-repairs', label: 'Stucco Repairs', icon: <Building2 className="h-8 w-8" /> },
    { id: 'driveway-replacement', label: 'Driveway Replacement', icon: <Building2 className="h-8 w-8" /> },
    { id: 'exterior-painting', label: 'Exterior Painting', icon: <Home className="h-8 w-8" /> },
    { id: 'tree-removal', label: 'Tree Removal Services', icon: <Home className="h-8 w-8" /> },
    { id: 'screen-enclosure', label: 'Screen Enclosure Repairs', icon: <Building2 className="h-8 w-8" /> },
    { id: 'pavers', label: 'Pavers', icon: <Building2 className="h-8 w-8" /> },
    { id: 'junk-removal', label: 'Junk Removal Services', icon: <Home className="h-8 w-8" /> }
  ];

  const propertyTypes = [
    { value: 'residential', label: 'Residential Home', desc: 'Single family home, condo, or townhouse' },
    { value: 'commercial', label: 'Commercial Property', desc: 'Office building, retail space, or warehouse' },
    { value: 'hoa', label: 'HOA/Community', desc: 'Homeowners association or community property' }
  ];

  const storyOptions = [
    { value: 'single', label: 'Single-level building' },
    { value: 'two', label: 'Two-story building' },
    { value: 'three', label: 'Three-story building' },
    { value: 'highrise', label: 'High-rise building' }
  ];

  const timelineOptions = [
    { value: 'urgent', label: 'Urgent — need a pro right away', desc: 'Within 48 hours' },
    { value: 'ready', label: 'Ready to hire, but not in a hurry', desc: 'Within 7 days' },
    { value: 'researching', label: 'Still researching', desc: 'No timeline in mind' }
  ];

  const insuranceOptions = [
    { value: 'approved', label: 'Yes, insurance claim has already been approved' },
    { value: 'pending', label: 'Insurance claim is pending approval' },
    { value: 'no', label: 'No, the project is not covered by an insurance claim' }
  ];

  const budgetOptions = [
    { value: 'under-500', label: 'Under $500' },
    { value: '500-1500', label: '$500 - $1,500' },
    { value: '1500-5000', label: '$1,500 - $5,000' },
    { value: '5000-15000', label: '$5,000 - $15,000' },
    { value: '15000-50000', label: '$15,000 - $50,000' },
    { value: 'over-50000', label: 'Over $50,000' }
  ];

  const repairTypeOptions = [
    'Roof Repairs Flashing, Mortar, Active Leaks (Shingles, Tile, Metal)',
    'Stucco Repairs / Concrete Slabs',
    'Driveway Repairs / Replacements',
    'Gutter Repairs / Replacements',
    'Soffit or Fascia Repairs / Replacements',
    'Tree(s) Removal',
    'Paint',
    'Siding',
    'Screen Enclosure',
    'Junk Removal',
    'Landscaping Projects',
    'Entire portions of roof need to be repaired'
  ];

  const totalSteps = 8;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    // Validate current step before proceeding
    if (currentStep === 1 && !formData.service) {
      toast.error('Please select a service to continue');
      return;
    }
    if (currentStep === 2 && !formData.property_type) {
      toast.error('Please select a property type');
      return;
    }
    if (currentStep === 3 && !formData.stories) {
      toast.error('Please select number of stories');
      return;
    }
    if (currentStep === 4 && !formData.timeline) {
      toast.error('Please select a timeline');
      return;
    }
    if (currentStep === 5 && !formData.insurance_claim) {
      toast.error('Please select an insurance option');
      return;
    }
    if (currentStep === 6 && !formData.budget_range) {
      toast.error('Please select a budget range');
      return;
    }
    if (currentStep === 7 && !formData.project_description.trim()) {
      toast.error('Please provide a project description');
      return;
    }
    
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate final step
    if (!formData.name || !formData.email || !formData.phone || !formData.zip_code) {
      toast.error('Please fill in all required contact fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          fd.append(k, JSON.stringify(v));
        } else if (v !== null && v !== undefined) {
          fd.append(k, String(v));
        }
      });
      (photos || []).slice(0, 5).forEach((file) => {
        fd.append('photos', file);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead/submit`, {
        method: 'POST',
        body: fd,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Thank you! We\'ll contact you shortly to schedule your FREE inspection.');
        // Reset form
        setFormData({
          service: '',
          property_type: '',
          stories: '',
          timeline: '',
          insurance_claim: '',
          budget_range: '',
          project_description: '',
          repair_types: [],
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zip_code: ''
        });
        setPhotos([]);
        setCurrentStep(1);
      } else {
        toast.error(result.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to submit. Please call us directly at (407) 785-0642.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRepairType = (type) => {
    setFormData(prev => ({
      ...prev,
      repair_types: prev.repair_types.includes(type)
        ? prev.repair_types.filter(t => t !== type)
        : [...prev.repair_types, type]
    }));
  };

  return (
    <Card ref={formRef} className="shadow-2xl border-2 border-[hsl(var(--primary))]">
      <CardHeader className="bg-[hsl(var(--primary))] text-white rounded-t-lg">
        <div className="flex items-center justify-between mb-2">
          <CardTitle className="text-lg sm:text-xl md:text-2xl font-bold">
            Step {currentStep} of {totalSteps}
          </CardTitle>
          <span className="text-xs sm:text-sm font-semibold bg-white/20 px-2 sm:px-3 py-1 rounded-full" data-testid="progress-percentage">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <Progress value={progress} className="h-2 bg-white/20" data-testid="progress-bar" />
      </CardHeader>

      <CardContent className="p-4 sm:p-6">
        {/* Step 1: Service Selection */}
        {currentStep === 1 && (
          <div className="space-y-4 sm:space-y-6" data-testid="step-1-service-selection">
            <div className="text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">What service do you need?</h3>
              <p className="text-sm sm:text-base text-slate-600">Select the primary service you're interested in</p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {services.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, service: service.label })}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all text-left hover:border-[hsl(var(--primary))] hover:shadow-md ${
                    formData.service === service.label
                      ? 'border-[hsl(var(--primary))] bg-[#e9f2fb] shadow-md'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`service-option-${service.id}`}
                >
                  <div className="text-[hsl(var(--primary))] mb-2">{service.icon}</div>
                  <div className="text-xs sm:text-sm font-semibold text-slate-900">{service.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Property Type */}
        {currentStep === 2 && (
          <div className="space-y-6" data-testid="step-2-property-type">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">What type of property?</h3>
              <p className="text-slate-600">This helps us match you with the right contractors</p>
            </div>
            <div className="space-y-3">
              {propertyTypes.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, property_type: option.value })}
                  className={`w-full p-5 rounded-lg border-2 transition-all text-left hover:border-[hsl(var(--primary))] hover:shadow-md ${
                    formData.property_type === option.value
                      ? 'border-[hsl(var(--primary))] bg-[#e9f2fb] shadow-md'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`property-type-${option.value}`}
                >
                  <div className="text-lg font-semibold text-slate-900 mb-1">{option.label}</div>
                  <div className="text-sm text-slate-600">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Number of Stories */}
        {currentStep === 3 && (
          <div className="space-y-6" data-testid="step-3-stories">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">How many stories is your property?</h3>
              <p className="text-slate-600">This helps us match you with the right contractors</p>
            </div>
            <div className="space-y-3">
              {storyOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, stories: option.value })}
                  className={`w-full p-5 rounded-lg border-2 transition-all text-left hover:border-[hsl(var(--primary))] hover:shadow-md ${
                    formData.stories === option.value
                      ? 'border-[hsl(var(--primary))] bg-[#e9f2fb] shadow-md'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`stories-${option.value}`}
                >
                  <div className="text-lg font-semibold text-slate-900">{option.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Timeline */}
        {currentStep === 4 && (
          <div className="space-y-6" data-testid="step-4-timeline">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">What's your timeline?</h3>
              <p className="text-slate-600">Timeline helps us prioritize your request</p>
            </div>
            <div className="space-y-3">
              {timelineOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, timeline: option.value })}
                  className={`w-full p-5 rounded-lg border-2 transition-all text-left hover:border-[hsl(var(--primary))] hover:shadow-md ${
                    formData.timeline === option.value
                      ? 'border-[hsl(var(--primary))] bg-[#e9f2fb] shadow-md'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`timeline-${option.value}`}
                >
                  <div className="text-lg font-semibold text-slate-900 mb-1">{option.label}</div>
                  <div className="text-sm text-slate-600">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Insurance Claim */}
        {currentStep === 5 && (
          <div className="space-y-6" data-testid="step-5-insurance">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Is this project covered by an insurance claim?</h3>
              <p className="text-slate-600">This helps us understand your project funding</p>
            </div>
            <div className="space-y-3">
              {insuranceOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, insurance_claim: option.value })}
                  className={`w-full p-5 rounded-lg border-2 transition-all text-left hover:border-[hsl(var(--primary))] hover:shadow-md ${
                    formData.insurance_claim === option.value
                      ? 'border-[hsl(var(--primary))] bg-[#e9f2fb] shadow-md'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`insurance-${option.value}`}
                >
                  <div className="text-lg font-semibold text-slate-900">{option.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Budget Range */}
        {currentStep === 6 && (
          <div className="space-y-6" data-testid="step-6-budget">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">What's your budget range?</h3>
              <p className="text-slate-600">This helps contractors provide accurate quotes</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {budgetOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, budget_range: option.value })}
                  className={`p-4 rounded-lg border-2 transition-all hover:border-[hsl(var(--primary))] hover:shadow-md ${
                    formData.budget_range === option.value
                      ? 'border-[hsl(var(--primary))] bg-[#e9f2fb] shadow-md'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`budget-${option.value}`}
                >
                  <div className="text-base font-semibold text-slate-900 text-center">{option.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 7: Project Details */}
        {currentStep === 7 && (
          <div className="space-y-6" data-testid="step-7-project-details">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Project Details</h3>
              <p className="text-slate-600">Tell us more about your specific needs</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="project-description" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Project Description
                </Label>
                <Textarea
                  id="project-description"
                  placeholder="e.g., roof leak, storm damage, etc."
                  value={formData.project_description}
                  onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                  rows={4}
                  className="resize-none"
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                  What types of repairs or maintenance are needed? (Select all that apply)
                </Label>
                <div className="space-y-2">
                  {repairTypeOptions.map((type, index) => (
                    <label
                      key={index}
                      className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`repair-type-${index}`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.repair_types.includes(type)}
                        onChange={() => toggleRepairType(type)}
                        className="mt-1 h-4 w-4 text-[hsl(var(--primary))] rounded border-slate-300 focus:ring-[hsl(var(--primary))]"
                      />
                      <span className="text-sm text-slate-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Optional: Photo uploader to help us understand scope */}
              <div className="pt-2">
                <ImageUploader
                  files={photos}
                  onChange={setPhotos}
                  maxFiles={5}
                  maxMb={10}
                  label="Attach photos (optional)"
                  hint="Photos of the damage, scope, or area help us prepare. Up to 5 images, 10MB each. JPG / PNG / WEBP / HEIC."
                  testId="questionnaire-photos"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 8: Contact Information */}
        {currentStep === 8 && (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="step-8-contact-info">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Contact Information</h3>
              <p className="text-slate-600">How can contractors reach you?</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="h-12"
                  data-testid="contact-phone-input"
                />
              </div>
              <AddressAutocomplete
                value={formData.address}
                onChange={(text) => setFormData((s) => ({ ...s, address: text }))}
                onPlaceSelected={(p) => setFormData((s) => ({
                  ...s,
                  address: p.formatted || p.street || s.address,
                  city: p.city || s.city,
                  state: p.state || s.state,
                  zip_code: p.zip_code || s.zip_code,
                }))}
                label="Property Address"
                required={true}
                testId="contact-address-input"
                placeholder="Start typing your property address..."
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label htmlFor="city" className="text-sm font-semibold text-slate-700 mb-2 block">City</Label>
                  <Input id="city" type="text" placeholder="City" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="h-12" data-testid="contact-city-input" />
                </div>
                <div>
                  <Label htmlFor="state" className="text-sm font-semibold text-slate-700 mb-2 block">State</Label>
                  <Input id="state" type="text" placeholder="FL" maxLength={2} value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })} className="h-12" data-testid="contact-state-input" />
                </div>
                <div>
                  <Label htmlFor="zip" className="text-sm font-semibold text-slate-700 mb-2 block">
                    Zip Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="zip"
                    type="text"
                    placeholder="ZIP"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    required
                    className="h-12"
                    data-testid="contact-zip-input"
                  />
                </div>
              </div>
            </div>
          </form>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-200">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1 || isSubmitting}
            className="h-10 sm:h-11 px-3 sm:px-6 text-sm sm:text-base"
            data-testid="questionnaire-previous-btn"
          >
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Previous</span>
          </Button>

          {currentStep < totalSteps ? (
            <Button
              type="button"
              onClick={handleNext}
              className="h-10 sm:h-11 px-4 sm:px-6 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-sm sm:text-base"
              data-testid="questionnaire-next-btn"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Continue</span>
              <ArrowRight className="h-4 w-4 sm:ml-2 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="h-10 sm:h-11 px-4 sm:px-6 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white font-bold text-sm sm:text-base"
              data-testid="questionnaire-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
